<template>
  <div class="list-page-container">
    <!-- 顶部操作区 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-setting"></i>
          <span>{{ $t('system.systemParamsTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleAdd" v-if="hasPermission('sys:params:save')">
            <i class="el-icon-plus"></i> {{ $t('paramManagement.add') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <el-form :inline="true" :model="searchForm" class="filter-form" @submit.native.prevent>
          <el-form-item>
            <el-input
              v-model="searchForm.paramCode"
              :placeholder="$t('paramManagement.searchPlaceholder')"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </el-form-item>
          <div class="search-btns">
            <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('common.search') }}</el-button>
            <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
          </div>
        </el-form>
      </div>
    </div>

    <!-- 数据列表 -->
    <el-card class="list-card" shadow="never">
      <el-table
        :data="paramsList"
        v-loading="loading"
        height="100%"
        style="width: 100%"
        @selection-change="handleSelectionChange"
        class="custom-table"
      >
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="paramCode" :label="$t('paramManagement.paramCode')" min-width="150"></el-table-column>
        <el-table-column prop="paramValue" :label="$t('paramManagement.paramValue')" min-width="200">
          <template slot-scope="scope">
            <span v-if="scope.row.valueType === 'boolean'">
              <el-tag :type="scope.row.paramValue === 'true' ? 'success' : 'info'">{{ scope.row.paramValue }}</el-tag>
            </span>
            <span v-else>{{ scope.row.paramValue }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="valueType" :label="$t('paramManagement.valueType')" width="100">
          <template slot-scope="scope">
            <el-tag size="small">{{ scope.row.valueType }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="paramType" :label="$t('paramManagement.paramType')" width="120">
          <template slot-scope="scope">
            <el-tag :type="scope.row.paramType === 0 ? 'danger' : 'info'" size="small">
              {{ scope.row.paramType === 0 ? $t('paramManagement.systemParam') : $t('paramManagement.nonSystemParam') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('paramManagement.remark')" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.operation')" :width="actionColumnWidth" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button type="warning" size="mini" plain icon="el-icon-edit" @click="handleEdit(scope.row)" v-if="hasPermission('sys:params:update')">
                {{ $t('common.edit') }}
              </el-button>
              <el-button type="danger" size="mini" plain icon="el-icon-delete" @click="handleDelete(scope.row)" v-if="hasPermission('sys:params:delete') && scope.row.paramType !== 0">
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      ></el-pagination>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="paramForm" :rules="rules" ref="paramForm" label-width="100px">
        <el-form-item :label="$t('paramManagement.paramCode')" prop="paramCode">
          <el-input v-model="paramForm.paramCode" :disabled="!!paramForm.id"></el-input>
        </el-form-item>
        <el-form-item :label="$t('paramManagement.paramValue')" prop="paramValue">
          <el-input v-model="paramForm.paramValue" type="textarea" :rows="3"></el-input>
        </el-form-item>
        <el-form-item :label="$t('paramManagement.valueType')" prop="valueType">
          <el-select v-model="paramForm.valueType" style="width: 100%">
            <el-option v-for="item in valueTypes" :key="item" :label="item" :value="item"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('paramManagement.remark')" prop="remark">
          <el-input v-model="paramForm.remark" type="textarea" :rows="2"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitLoading">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'SystemParams',
  data() {
    return {
      loading: false,
      submitLoading: false,
      paramsList: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      searchForm: {
        paramCode: ''
      },
      selectedItems: [],
      dialogVisible: false,
      dialogTitle: '',
      paramForm: {
        id: null,
        paramCode: '',
        paramValue: '',
        valueType: 'string',
        remark: ''
      },
      valueTypes: ['string', 'number', 'boolean', 'json', 'array']
    }
  },
  computed: {
    visibleActionCount() {
      let count = 0
      if (this.hasPermission('sys:params:update')) count += 1
      if (this.hasPermission('sys:params:delete')) count += 1
      return count
    },
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170 }
      return widthMap[count] || 0
    },
    rules() {
      return {
        paramCode: [{ required: true, message: this.$t('paramManagement.paramCodeRequired'), trigger: 'blur' }],
        paramValue: [{ required: true, message: this.$t('paramManagement.paramValueRequired'), trigger: 'blur' }],
        valueType: [{ required: true, message: this.$t('paramManagement.valueTypeRequired'), trigger: 'change' }]
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    hasPermission,
    async getList() {
      this.loading = true
      try {
        const res = await request({
          url: '/admin/params/page',
          method: 'get',
          params: {
            page: this.currentPage,
            limit: this.pageSize,
            paramCode: this.searchForm.paramCode
          }
        })
        if (res) {
          this.paramsList = res.list || []
          this.total = res.total || 0
        }
      } catch (error) {
        console.error('Fetch params error:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.paramCode = ''
      this.handleSearch()
    },
    handleSelectionChange(val) {
      this.selectedItems = val
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    handleAdd() {
      this.dialogTitle = this.$t('paramManagement.addParam')
      this.paramForm = {
        id: null,
        paramCode: '',
        paramValue: '',
        valueType: 'string',
        remark: ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.paramForm) {
          this.$refs.paramForm.clearValidate()
        }
      })
    },
    handleEdit(row) {
      this.dialogTitle = this.$t('paramManagement.editParam')
      this.paramForm = { ...row }
      this.dialogVisible = true
    },
    async submitForm() {
      this.$refs.paramForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            const isEdit = !!this.paramForm.id
            const res = await request({
              url: '/admin/params',
              method: isEdit ? 'put' : 'post',
              data: this.paramForm
            })
            if (res !== undefined) {
              this.$message.success(isEdit ? this.$t('paramManagement.updateSuccess') : this.$t('paramManagement.addSuccess'))
              this.dialogVisible = false
              this.getList()
            }
          } catch (error) {
            console.error('Submit param error:', error)
          } finally {
            this.submitLoading = false
          }
        }
      })
    },
    handleDelete(row) {
      this.$confirm(this.$t('common.confirmDelete'), this.$t('common.tips'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        try {
          const res = await request({
            url: '/admin/params/delete',
            method: 'post',
            data: [row.id]
          })
          if (res !== undefined) {
            this.$message.success(this.$t('common.deleteSuccess'))
            this.getList()
          }
        } catch (error) {
          console.error('Delete param error:', error)
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 系统参数管理页面特有样式 */
.list-page-container {
  .delete-btn {
    color: #ef4444;
    &:hover {
      color: #dc2626;
    }
  }
}

::v-deep {
  .el-dialog {
    border-radius: 12px;
    overflow: hidden;
    
    .el-dialog__header {
      background-color: #f8fafc;
      padding: 15px 20px;
      border-bottom: 1px solid #f1f5f9;
      
      .el-dialog__title {
        font-size: 15px;
        font-weight: 600;
        color: #1e293b;
      }
    }
    
    .el-dialog__body {
      padding: 20px 25px;
    }
    
    .el-dialog__footer {
      padding: 15px 20px;
      border-top: 1px solid #f1f5f9;
    }

    .el-input__inner, .el-textarea__inner, .el-select {
      border-radius: 8px;
    }
  }
}
</style>
